// src/pages/organization/allplayers.js

import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useMainPageTitle } from "../../contexts/PageContext";
import ProfileImage from "../../components/profileImage";
import { Link } from "react-router-dom";

const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";

const AllPlayers = () => {
  const { t } = useTranslation();
  const { updatePageTitle } = useMainPageTitle();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  const fetchPlayers = useCallback(async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...(searchTerm && { search: searchTerm }),
      });

      const response = await fetch(
        `${API_URL}/organization/all-players-by-organization?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.errors || "Failed to fetch players");
      }

      const data = await response.json();
      console.log("Received players:", data.players?.length || 0);

      if (!data.players) {
        throw new Error("Invalid response format");
      }

      setPlayers(data.players);
      setPagination(data.pagination);
      setError(null);
    } catch (err) {
      console.error("Error fetching players:", err);
      setError(
        err.message || "Failed to load players. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchTerm]);

  useEffect(() => {
    updatePageTitle(t("players.allPlayers"), t("players.playersList"));
    fetchPlayers();
  }, [fetchPlayers, updatePageTitle, t]);

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, page: 1 })); // Reset to first page on search
  };

  if (loading && !players.length) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#F15213]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-center">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            {t("players.allPlayers")}
          </h1>
          <div className="text-sm text-gray-500">
            {pagination.total}{" "}
            {pagination.total === 1
              ? t("players.player")
              : t("players.players")}
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <input
              type="text"
              placeholder={t("players.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full px-4 py-2 pl-10 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#F15213] focus:border-[#F15213] transition-colors duration-200"
            />
            <svg
              className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Players Grid */}
      {players.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {players.map((player) => (
              <Link
                key={player.id || player._id}
                to={`/organization/player/${player.id || player._id}`}
                className="block bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow duration-200 hover:border-[#F15213]"
              >
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <ProfileImage
                      image={player.profilePicture}
                      alt={`${player.firstName} ${player.lastName}`}
                      className="h-16 w-16 rounded-full"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 truncate">
                      {player.firstName} {player.lastName}
                    </h3>
                    <p className="text-sm text-gray-500 truncate">
                      @{player.userName}
                    </p>
                    <p className="text-sm text-gray-500 truncate">
                      {player.email}
                    </p>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">
                      {t("players.joined")}{" "}
                      {new Date(player.createdAt).toLocaleDateString()}
                    </span>
                    {player.gender && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">
                        {player.gender}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {player.teamName && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                        {player.teamName}
                      </span>
                    )}
                    <span className="px-2 py-1 bg-[#F15213] text-white rounded-full text-xs">
                      {player.accountType}
                    </span>
                  </div>
                </div>
                {player.metaData?.birthday && (
                  <div className="mt-2 text-sm text-gray-500">
                    Birthday:{" "}
                    {new Date(player.metaData.birthday).toLocaleDateString()}
                  </div>
                )}
              </Link>
            ))}
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="mt-8 flex justify-center">
              <nav className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-3 py-1 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-3 py-1 text-gray-700">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="px-3 py-1 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </nav>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <svg
            className="h-12 w-12 mx-auto text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            No players found
          </h3>
          <p className="mt-2 text-gray-500">
            {searchTerm
              ? "Try adjusting your search terms"
              : "There are no players in your organization yet"}
          </p>
        </div>
      )}
    </div>
  );
};

export default AllPlayers;
